import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../api';
import { useLang } from '../context/LangContext';
import AuditeeMultiSelect from '../components/AuditeeMultiSelect';
import ControlMultiSelect from '../components/ControlMultiSelect';
import { formatRefs } from '../controlRefs';

// Frameworks met een standaardplanning (backend/auditPlanTemplates.js)
const TEMPLATE_FRAMEWORKS = ['iso27001'];
const KINDS = ['session', 'meeting', 'break', 'other'];
const KIND_BADGE = { session: 'bg-primary', meeting: 'bg-info text-dark', break: 'bg-light text-muted border', other: 'bg-secondary' };

const EMPTY_ITEM = {
  kind: 'session', day: '', start_time: '', end_time: '', block: '', code: '', title: '',
  topics: '', attention: '', auditee_note: '', location: '', control_ids: [], related_ids: [], contact_ids: [],
};

function fmtDay(day, lang) {
  if (!day) return null;
  return new Date(`${day}T12:00:00Z`).toLocaleDateString(lang === 'en' ? 'en-GB' : 'nl-BE',
    { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function ItemModal({ item, items, controls, contacts, onClose, onSaved, auditId, t }) {
  const [form, setForm] = useState({ ...EMPTY_ITEM, ...item });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const isSession = form.kind === 'session';

  // Controls die al als hoofdsessie in een ándere sessie zitten → lichtgrijs + sessiecode
  const usedElsewhere = new Map();
  for (const it of items) {
    if (it.id === item.id || it.kind !== 'session') continue;
    for (const cid of it.control_ids) usedElsewhere.set(cid, [...(usedElsewhere.get(cid) || []), it.code || it.title]);
  }

  async function save() {
    if (!form.title.trim()) { setError(`${t('plan_item_title')}?`); return; }
    setSaving(true);
    const body = {
      ...form,
      control_ids: isSession ? form.control_ids : [],
      related_ids: isSession ? form.related_ids.filter(id => !form.control_ids.includes(id)) : [],
    };
    try {
      if (item.id) await api.put(`/api/audits/${auditId}/plan/items/${item.id}`, body);
      else await api.post(`/api/audits/${auditId}/plan/items`, body);
      onSaved();
    } catch (e) {
      setError(e.response?.data?.error || t('error'));
      setSaving(false);
    }
  }

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{item.id ? t('edit') : t('plan_add')}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={saving} />
            </div>
            <div className="modal-body">
              <div className="row g-2 mb-3">
                <div className="col-md-3">
                  <label className="form-label small fw-medium mb-1">{t('plan_kind')}</label>
                  <select className="form-select form-select-sm" value={form.kind} onChange={set('kind')}>
                    {KINDS.map(k => <option key={k} value={k}>{t(`plan_kind_${k}`)}</option>)}
                  </select>
                </div>
                <div className="col-md-3">
                  <label className="form-label small fw-medium mb-1">{t('plan_day')}</label>
                  <input type="date" className="form-control form-control-sm" value={form.day || ''} onChange={set('day')} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-medium mb-1">{t('plan_start')}</label>
                  <input type="time" className="form-control form-control-sm" value={form.start_time || ''} onChange={set('start_time')} />
                </div>
                <div className="col-md-2">
                  <label className="form-label small fw-medium mb-1">{t('plan_end')}</label>
                  <input type="time" className="form-control form-control-sm" value={form.end_time || ''} onChange={set('end_time')} />
                </div>
                {isSession && (
                  <div className="col-md-2">
                    <label className="form-label small fw-medium mb-1">{t('plan_code')}</label>
                    <input className="form-control form-control-sm" placeholder="S1" value={form.code || ''} onChange={set('code')} />
                  </div>
                )}
              </div>

              <div className="mb-3">
                <label className="form-label small fw-medium mb-1">{t('plan_item_title')}</label>
                <input className="form-control form-control-sm" value={form.title} onChange={set('title')} />
              </div>

              {isSession && (
                <div className="mb-3">
                  <label className="form-label small fw-medium mb-1">{t('plan_block')}</label>
                  <input className="form-control form-control-sm" value={form.block || ''} onChange={set('block')} />
                </div>
              )}

              {form.kind !== 'break' && (
                <div className="mb-3">
                  <label className="form-label small fw-medium mb-1">{t('plan_topics')}</label>
                  <textarea className="form-control form-control-sm" rows={isSession ? 6 : 3}
                    value={form.topics || ''} onChange={set('topics')} />
                  <div className="form-text" style={{ fontSize: 11 }}>{t('plan_lines_hint')}</div>
                </div>
              )}

              {isSession && (
                <>
                  <div className="mb-3">
                    <label className="form-label small fw-medium mb-1">
                      {t('plan_controls')} <span className="text-muted fw-normal">({form.control_ids.length})</span>
                    </label>
                    <ControlMultiSelect controls={controls} value={form.control_ids} usedElsewhere={usedElsewhere}
                      onChange={ids => setForm(f => ({ ...f, control_ids: ids }))}
                      placeholder={t('plan_controls_none')} searchPlaceholder={t('plan_controls_search')} idPrefix="plan-ctrl" />
                    <div className="form-text" style={{ fontSize: 11 }}>{t('plan_controls_hint')}</div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-medium mb-1">
                      {t('plan_related')} <span className="text-muted fw-normal">({form.related_ids.length})</span>
                    </label>
                    <ControlMultiSelect controls={controls} value={form.related_ids} usedElsewhere={usedElsewhere}
                      disabledIds={new Set(form.control_ids)}
                      onChange={ids => setForm(f => ({ ...f, related_ids: ids }))}
                      placeholder={t('plan_controls_none')} searchPlaceholder={t('plan_controls_search')} idPrefix="plan-rel" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label small fw-medium mb-1">{t('plan_attention')}</label>
                    <textarea className="form-control form-control-sm" rows={2}
                      value={form.attention || ''} onChange={set('attention')} />
                  </div>
                </>
              )}

              {form.kind !== 'break' && (
                <div className="row g-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-medium mb-1 d-block">{t('auditees')}</label>
                    <AuditeeMultiSelect contacts={contacts} value={form.contact_ids}
                      onChange={ids => setForm(f => ({ ...f, contact_ids: ids }))}
                      placeholder={t('auditees_none')} idPrefix="plan-auditee" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium mb-1">{t('plan_auditee_note')}</label>
                    <input className="form-control form-control-sm" value={form.auditee_note || ''} onChange={set('auditee_note')} />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-medium mb-1">{t('plan_item_location')}</label>
                    <input className="form-control form-control-sm" value={form.location || ''} onChange={set('location')} />
                  </div>
                </div>
              )}
              {error && <div className="alert alert-danger py-2 mt-3 mb-0 small">{error}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
                {saving && <span className="spinner-border spinner-border-sm me-2" />}{t('save')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

// Minuten → "2 u 15 min"
function fmtMin(min, t) {
  const h = Math.floor(min / 60), m = min % 60;
  return h ? (m ? `${h} ${t('plan_gen_h')} ${m} min` : `${h} ${t('plan_gen_h')}`) : `${m} min`;
}

// Parameters van de standaardplanning (zie backend/planGenerator.js)
const GEN_NUMBER_FIELDS = {
  rhythm: [['lunch_min', 'plan_gen_lunch_min'], ['coffee_min', 'plan_gen_coffee_min'], ['processing_min', 'plan_gen_processing_min']],
  meetings: [['opening_min', 'plan_gen_opening_min'], ['daystart_min', 'plan_gen_daystart_min'], ['feedback_min', 'plan_gen_feedback_min'],
    ['closing_prep_min', 'plan_gen_closing_prep_min'], ['closing_min', 'plan_gen_closing_min']],
};

function TemplateModal({ auditId, defaultDate, savedParams, hasItems, onClose, onDone, t }) {
  const [params, setParams] = useState(null);
  const [preview, setPreview] = useState(null);
  const [replace, setReplace] = useState(hasItems);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Startwaarden: laatst gebruikte parameters, aangevuld met de standaard van de server
  useEffect(() => {
    const init = { ...(savedParams || {}) };
    if (!init.start_date && defaultDate) init.start_date = defaultDate;
    api.post(`/api/audits/${auditId}/plan/template`, { params: init, dry_run: true })
      .then(r => { setParams(r.data.params); setPreview(r.data); })
      .catch(e => { setParams(init); setError(e.response?.data?.error || t('error')); });
  }, [auditId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Berekening opnieuw bij elke wijziging (vertraagd)
  useEffect(() => {
    if (!params) return;
    const timer = setTimeout(() => {
      api.post(`/api/audits/${auditId}/plan/template`, { params, dry_run: true })
        .then(r => { setPreview(r.data); setError(''); })
        .catch(e => { setPreview(null); setError(e.response?.data?.error || t('error')); });
    }, 350);
    return () => clearTimeout(timer);
  }, [params, auditId]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setParams(p => ({ ...p, [k]: e.target.value }));

  async function load() {
    setBusy(true);
    try {
      const r = await api.post(`/api/audits/${auditId}/plan/template`, { params, replace });
      onDone(r.data);
    } catch (e) {
      setError(e.response?.data?.error || t('error'));
      setBusy(false);
    }
  }

  const sum = preview?.summary;
  const numInput = (k, label, o = {}) => (
    <div className="col-6 col-md-4" key={k}>
      <label className="form-label small mb-1">{t(label)}</label>
      <input type="number" min="0" className="form-control form-control-sm" value={params[k] ?? ''} onChange={set(k)} {...o} />
    </div>
  );
  const timeInput = (k, label) => (
    <div className="col-6 col-md-4" key={k}>
      <label className="form-label small mb-1">{t(label)}</label>
      <input type="time" step="900" className="form-control form-control-sm" value={params[k] ?? ''} onChange={set(k)} />
    </div>
  );

  return (
    <>
      <div className="modal show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{t('plan_template')}</h5>
              <button type="button" className="btn-close" onClick={onClose} disabled={busy} />
            </div>
            <div className="modal-body">
              <p className="small text-muted">{t('plan_template_intro')}</p>
              {!params ? (
                <div className="text-muted small"><span className="spinner-border spinner-border-sm me-2" />{t('loading')}</div>
              ) : (
                <>
                  <div className="row g-2 mb-3">
                    <div className="col-6 col-md-4">
                      <label className="form-label small fw-medium mb-1">{t('plan_template_start')}</label>
                      <input type="date" required className={`form-control form-control-sm ${params.start_date ? '' : 'is-invalid'}`}
                        value={params.start_date || ''} onChange={set('start_date')} />
                      {!params.start_date && <div className="invalid-feedback">{t('plan_gen_start_required')}</div>}
                    </div>
                    {numInput('total_hours', 'plan_gen_total_hours', { step: 0.25 })}
                    {numInput('audit_pct', 'plan_gen_audit_pct', { max: 100, step: 5 })}
                    <div className="col-6 col-md-4">
                      <label className="form-label small mb-1">{t('plan_execution')}</label>
                      <select className="form-select form-select-sm" value={params.execution} onChange={set('execution')}>
                        {['onsite', 'hybrid', 'remote'].map(x => <option key={x} value={x}>{t(`plan_exec_${x}`)}</option>)}
                      </select>
                    </div>
                    {numInput('prep_pct', 'plan_gen_prep_pct', { max: 100, step: 5 })}
                  </div>

                  <div className="text-muted mb-1" style={{ fontSize: 11 }}>{t('plan_gen_rhythm').toUpperCase()}</div>
                  <div className="row g-2 mb-3">
                    {timeInput('day_start', 'plan_gen_day_start')}
                    {timeInput('day_end', 'plan_gen_day_end')}
                    {timeInput('lunch_start', 'plan_gen_lunch_start')}
                    {GEN_NUMBER_FIELDS.rhythm.map(([k, l]) => numInput(k, l, { step: 15 }))}
                  </div>

                  <div className="text-muted mb-1" style={{ fontSize: 11 }}>{t('plan_gen_meetings').toUpperCase()}</div>
                  <div className="row g-2 mb-3">
                    {GEN_NUMBER_FIELDS.meetings.map(([k, l]) => numInput(k, l, { step: 15 }))}
                  </div>

                  {sum && (
                    <div className="border rounded p-2 small bg-light">
                      <div className="fw-medium mb-1">
                        {sum.days} {t(sum.days === 1 ? 'plan_gen_day' : 'plan_gen_days')}
                        <span className="text-muted fw-normal"> · {t('plan_gen_day_ends')} {sum.day_ends.join(', ')}</span>
                      </div>
                      <div>{t('plan_gen_audit_time')}: <strong>{fmtMin(sum.audit_min, t)}</strong> ({Math.round(sum.audit_min / sum.total_min * 100)}%)
                        <span className="text-muted"> – {t('plan_gen_sessions')} {fmtMin(sum.session_min, t)}, {t('plan_gen_meetings_short')} {fmtMin(sum.meeting_min, t)}</span>
                      </div>
                      <div>{t('plan_gen_own_time')}: <strong>{fmtMin(sum.own_min, t)}</strong>
                        <span className="text-muted"> – {t('plan_gen_prep')} {fmtMin(sum.prep_min, t)}, {t('plan_gen_processing')} {fmtMin(sum.processing_min, t)}, {t('plan_gen_reporting')} {fmtMin(sum.reporting_min, t)}</span>
                      </div>
                      <details className="mt-1">
                        <summary className="text-muted">
                          {t('plan_gen_scope')}: {preview.sessions.length} {t('plan_gen_sessions')}
                          {preview.dropped.length > 0 && <>, {preview.dropped.length} {t('plan_gen_dropped')}</>}
                        </summary>
                        <ul className="mb-0 ps-3 mt-1">
                          {preview.sessions.map(x => (
                            <li key={x.label}>{x.label} <span className="text-muted">– {x.controls}/{x.of} controls</span></li>
                          ))}
                          {preview.dropped.map(x => (
                            <li key={x} className="text-muted"><s>{x}</s> – {t('plan_gen_no_controls')}</li>
                          ))}
                        </ul>
                      </details>
                      {preview.unplanned.length > 0 && (
                        <div className="text-warning-emphasis mt-1">
                          <i className="bi bi-exclamation-triangle me-1" />{t('plan_gen_unplanned')}: {preview.unplanned.join(', ')}
                        </div>
                      )}
                      {preview.warnings.map((w, i) => <div key={i} className="text-warning-emphasis mt-1"><i className="bi bi-exclamation-triangle me-1" />{w}</div>)}
                    </div>
                  )}

                  {hasItems && (
                    <div className="form-check mt-3">
                      <input className="form-check-input" type="checkbox" id="tpl-replace"
                        checked={replace} onChange={e => setReplace(e.target.checked)} />
                      <label className="form-check-label small" htmlFor="tpl-replace">{t('plan_template_replace')}</label>
                    </div>
                  )}
                </>
              )}
              {error && <div className="alert alert-danger py-2 mt-3 mb-0 small">{error}</div>}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={onClose} disabled={busy}>{t('cancel')}</button>
              <button type="button" className="btn btn-primary" onClick={load} disabled={busy || !preview || !!error || !params?.start_date}>
                {busy && <span className="spinner-border spinner-border-sm me-2" />}{t('plan_template')}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
}

const PLAN_TEXT_FIELDS = [
  ['criteria', 'plan_criteria'], ['objectives', 'plan_objectives'], ['method', 'plan_method'],
  ['documents', 'plan_documents'], ['focus', 'plan_focus'],
];

export default function AuditPlanPage() {
  const { id } = useParams();
  const { t, lang } = useLang();

  const [audit, setAudit]       = useState(null);
  const [data, setData]         = useState(null);
  const [contacts, setContacts] = useState([]);
  const [header, setHeader]     = useState({});
  const [headerDirty, setHeaderDirty] = useState(false);
  const [headerOpen, setHeaderOpen]   = useState(false);
  const [editing, setEditing]   = useState(null);
  const [tplOpen, setTplOpen]   = useState(false);
  const [notice, setNotice]     = useState(null);
  const [wordLoading, setWordLoading] = useState(false);

  const load = useCallback(() => {
    return api.get(`/api/audits/${id}/plan`).then(r => {
      setData(r.data);
      setHeader(r.data.plan || {});
      setHeaderDirty(false);
    });
  }, [id]);

  useEffect(() => {
    api.get(`/api/audits/${id}`).then(r => {
      setAudit(r.data);
      api.get(`/api/clients/${r.data.client_id}/contacts`).then(c => setContacts(c.data)).catch(() => {});
    });
    load();
  }, [id, load]);

  async function saveHeader() {
    await api.put(`/api/audits/${id}/plan`, header);
    setHeaderDirty(false);
    setNotice({ type: 'success', text: t('plan_saved') });
  }

  async function removeItem(it) {
    if (!window.confirm(t('plan_delete_confirm'))) return;
    await api.delete(`/api/audits/${id}/plan/items/${it.id}`);
    load();
  }

  async function handleWord() {
    setWordLoading(true);
    try {
      const res = await api.post(`/api/audits/${id}/plan/docx`, {}, { responseType: 'blob' });
      const url = URL.createObjectURL(
        new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' })
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = `auditplan-${(audit.client_name || id).toString().replace(/[^\w-]+/g, '_')}.docx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch {
      alert(t('error'));
    } finally {
      setWordLoading(false);
    }
  }

  if (!audit || !data) return <div className="page-body">{t('loading')}</div>;

  const { items, controls, coverage } = data;
  const setH = (k) => (e) => { setHeader(h => ({ ...h, [k]: e.target.value })); setHeaderDirty(true); };
  const days = [...new Set(items.map(i => i.day || ''))];
  const hasTemplate = TEMPLATE_FRAMEWORKS.includes(audit.framework_code);
  const pct = coverage.total ? Math.round((coverage.covered / coverage.total) * 100) : 0;

  return (
    <>
      <div className="page-header">
        <div>
          <div className="text-muted-sm mb-1">
            <Link to="/audits" className="text-muted text-decoration-none">{t('nav_audits')}</Link>
            {' / '}
            <Link to={`/audits/${id}`} className="text-muted text-decoration-none">{audit.name}</Link>
            {' / '}
          </div>
          <h2>{t('plan_title')}</h2>
        </div>
        <div className="d-flex gap-2">
          {hasTemplate && (
            <button className="btn btn-outline-secondary btn-sm" onClick={() => setTplOpen(true)}>
              <i className="bi bi-magic me-1" />{t('plan_template')}
            </button>
          )}
          <button className="btn btn-outline-primary btn-sm" onClick={handleWord} disabled={wordLoading || items.length === 0}>
            {wordLoading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-file-word me-1" />{t('plan_word')}</>}
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => setEditing({ ...EMPTY_ITEM, day: days.filter(Boolean).slice(-1)[0] || '' })}>
            <i className="bi bi-plus-lg me-1" />{t('plan_add')}
          </button>
        </div>
      </div>

      <div className="page-body">
        {notice && (
          <div className={`alert alert-${notice.type} py-2 small d-flex justify-content-between`}>
            <span>{notice.text}</span>
            <button className="btn-close btn-sm" onClick={() => setNotice(null)} />
          </div>
        )}

        {/* Dekking */}
        <div className="card mb-4">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold small"><i className="bi bi-grid-3x3-gap me-1" />{t('plan_coverage')}</span>
              <span className="small">{coverage.covered} / {coverage.total}</span>
            </div>
            <div className="progress mb-2" style={{ height: 6 }}>
              <div className="progress-bar" style={{ width: `${pct}%`, background: pct === 100 ? '#16a34a' : undefined }} />
            </div>
            {coverage.uncovered_ids.length > 0
              ? <div className="small"><span className="text-muted">{t('plan_uncovered')}: </span>{formatRefs(coverage.uncovered_ids, controls)}</div>
              : coverage.total > 0 && <div className="small text-success"><i className="bi bi-check-circle me-1" />{t('plan_all_covered')}</div>}
            {coverage.out_of_scope > 0 && (
              <div className="small text-warning mt-1"><i className="bi bi-exclamation-triangle me-1" />{coverage.out_of_scope} {t('plan_out_of_scope')}</div>
            )}
          </div>
        </div>

        {/* Kopgegevens */}
        <div className="card mb-4">
          <div className="card-header bg-white d-flex justify-content-between align-items-center"
            style={{ cursor: 'pointer' }} onClick={() => setHeaderOpen(o => !o)}>
            <span className="fw-semibold small"><i className={`bi bi-chevron-${headerOpen ? 'down' : 'right'} me-1`} />{t('plan_header')}</span>
            {headerDirty && <span className="badge bg-warning text-dark">●</span>}
          </div>
          {headerOpen && (
            <div className="card-body">
              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-medium mb-1">{t('plan_audit_type')}</label>
                  <input className="form-control form-control-sm" value={header.audit_type || ''} onChange={setH('audit_type')} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium mb-1">{t('plan_execution')}</label>
                  <select className="form-select form-select-sm" value={header.execution || 'onsite'} onChange={setH('execution')}>
                    {['onsite', 'hybrid', 'remote'].map(k => <option key={k} value={k}>{t(`plan_exec_${k}`)}</option>)}
                  </select>
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium mb-1">{t('plan_location')}</label>
                  <input className="form-control form-control-sm" value={header.location || ''} onChange={setH('location')} />
                </div>
                <div className="col-md-6">
                  <label className="form-label small fw-medium mb-1">{t('plan_contact')}</label>
                  <input className="form-control form-control-sm" value={header.contact_person || ''} onChange={setH('contact_person')} />
                </div>
              </div>
              {PLAN_TEXT_FIELDS.map(([k, label]) => (
                <div className="mb-3" key={k}>
                  <label className="form-label small fw-medium mb-1">{t(label)}</label>
                  <textarea className="form-control form-control-sm" rows={3} value={header[k] || ''} onChange={setH(k)} />
                </div>
              ))}
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-primary btn-sm" onClick={saveHeader} disabled={!headerDirty}>{t('save')}</button>
                <span className="text-muted" style={{ fontSize: 11 }}>{t('plan_lines_hint')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Agenda */}
        {items.length === 0 && (
          <div className="card"><div className="card-body text-muted small">{t('plan_empty')}</div></div>
        )}
        {days.map(day => (
          <div className="card mb-3" key={day || 'none'}>
            <div className="card-header bg-white fw-semibold small">
              <i className="bi bi-calendar3 me-2" />
              {day ? fmtDay(day, lang) : t('plan_no_day')}
            </div>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-0 align-middle">
                <tbody>
                  {items.filter(i => (i.day || '') === day).map(it => (
                    <tr key={it.id} className={it.kind === 'break' ? 'table-light' : ''} style={{ cursor: 'pointer' }}
                      onClick={() => setEditing(it)}>
                      <td className="text-nowrap small" style={{ width: 110 }}>
                        {it.start_time}{it.end_time ? `–${it.end_time}` : ''}
                      </td>
                      <td style={{ width: 90 }}>
                        <span className={`badge ${KIND_BADGE[it.kind]}`}>{it.code || t(`plan_kind_${it.kind}`)}</span>
                      </td>
                      <td>
                        <div className={it.kind === 'break' ? 'text-muted small' : 'fw-medium small'}>{it.title}</div>
                        {it.block && <div className="text-muted" style={{ fontSize: 11 }}>{it.block}</div>}
                        {it.kind === 'session' && (it.control_ids.length > 0 || it.related_ids.length > 0) && (
                          <div className="text-muted" style={{ fontSize: 11 }}>
                            {formatRefs(it.control_ids, controls)}
                            {it.related_ids.length > 0 && <> · {t('plan_related').toLowerCase()}: {formatRefs(it.related_ids, controls)}</>}
                          </div>
                        )}
                      </td>
                      <td className="small text-muted" style={{ width: 220 }}>
                        {it.contacts.map(c => c.name).join(', ')}
                        {it.auditee_note && <div style={{ fontSize: 11 }}><em>{it.auditee_note}</em></div>}
                      </td>
                      <td style={{ width: 40 }} onClick={e => e.stopPropagation()}>
                        <button className="btn btn-sm btn-link text-danger p-0" title={t('delete')} onClick={() => removeItem(it)}>
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <ItemModal item={editing} items={items} controls={controls} contacts={contacts} auditId={id} t={t}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }} />
      )}
      {tplOpen && (
        <TemplateModal auditId={id} t={t} hasItems={items.length > 0} savedParams={data.plan?.gen_params}
          defaultDate={audit.audit_date ? String(audit.audit_date).slice(0, 10) : ''}
          onClose={() => setTplOpen(false)}
          onDone={(r) => {
            setTplOpen(false);
            setNotice({
              type: r.unplanned?.length ? 'warning' : 'success',
              text: `${t('plan_template_done')} (${r.items}).${r.unplanned?.length ? ` ${t('plan_gen_unplanned')}: ${r.unplanned.join(', ')}` : ''}`,
            });
            load();
          }} />
      )}
    </>
  );
}
